import React from 'react';
import PropTypes from 'prop-types';
import { withPrefix } from 'gatsby';

import Layout from '../components/layout';
import SEO from '../components/seo';
import { Box, Link as HouzzLink, Text, TextHeading, TextBody, TextNotation } from '@houzz/ui';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from '@houzz/ui/tabs';
import {} from 'gatsby';

const SectionHeader = ({ children }) => (
    <TextHeading level={2} size="xSmall" my="M">
        {children}
    </TextHeading>
);

SectionHeader.propTypes = {
    children: PropTypes.node,
};

const Introduction = () => (
    <Layout>
        <SEO title="Introduction" />
        <Box as="article" className="container" width="85%" margin="auto" mt="XXXL" mb="XXL">
            <Text as="h1" size="xLarge">
                Introduction
            </Text>
            <Text as="p">Learn how to use components, and how to contribute to Houzz UI.</Text>

            <Tabs className="extraClass">
                <TabList>
                    <Tab>Design System Basics</Tab>
                    <Tab>For Designers</Tab>
                    <Tab>For Engineers</Tab>
                </TabList>
                <TabPanels>
                    <TabPanel>
                        <Box as="section" py="L">
                            <SectionHeader>What is a Design System?</SectionHeader>
                            <TextBody as="p" size="medium">
                                A Design System is a framework of practices and processes for people who build products.
                                It is a platform to identify, decide on, and document what to share, whether a visual
                                style, design patterns, front-end UI components, and practices like accessibility,
                                research, content strategy. It is comprised of three interrelated systems you’ll need to
                                solve for to be successful:
                            </TextBody>
                            <ol>
                                <li>
                                    <TextBody>
                                        <strong>A kit of reusable, interconnected parts:</strong> A design system offers
                                        a library of visual style and components documented and released as reusable
                                        code for developers and/or tool(s) for designers. It may also offer guidance on
                                        accessibility, page layout, and editorial and less often branding, data viz, UX
                                        patterns, and other tools.
                                    </TextBody>
                                </li>
                                <li>
                                    <TextBody>
                                        <strong>A set of cohesive, interconnected products</strong>: A design system is
                                        adopted by and supported for other teams making experiences. These teams use it
                                        to develop and ship features more efficiently to form a more cohesive customer
                                        journey.
                                    </TextBody>
                                </li>
                                <li>
                                    <TextBody>
                                        <strong>A community of collaborative, interconnected people:</strong> A design
                                        system is made by an individual, team, and/or community. While some arise less
                                        formally, organizations now dedicate small to large squad(s) to develop and
                                        release system versions and processes over time.
                                    </TextBody>
                                </li>
                            </ol>
                            <TextNotation as="p">From “Houzz Design System Report” 2018 by Jackie Ngo</TextNotation>

                            <SectionHeader>Houzz Design System Goals</SectionHeader>
                            <TextBody>
                                What would the Houzz product look like if it was designed by one designer with unlimited
                                time and resources? This type of coordination would help us achieve:
                            </TextBody>
                            <ol>
                                <li>
                                    <TextBody>Speed and efficiency designing and implementing features</TextBody>
                                </li>
                                <li>
                                    <TextBody>
                                        1:1 alignment between design component library and engineering component library
                                    </TextBody>
                                </li>
                                <li>
                                    <TextBody>
                                        Houzz brand and product alignment between Houzz and Houzz Pro (Each product
                                        being part of the same family)
                                    </TextBody>
                                </li>
                            </ol>

                            <SectionHeader>What&rsquo;s next?</SectionHeader>
                            <TextBody>
                                In our initial release we’ve defined and developed a series of atom level components and
                                foundations to use in Houzz. We plan to continue building out our infrastructure of
                                components and look for ways to build out our component library with molecules,
                                organisms, page templates etc.
                            </TextBody>
                        </Box>
                    </TabPanel>
                    <TabPanel>
                        <Box as="section" py="L">
                            <SectionHeader>Atomic Design</SectionHeader>
                            <TextBody>
                                Atomic design is a methodology for creating design systems. Atomic design uses chemistry
                                as inspiration to define how UI elements interact with each other to create basic and
                                complex components.
                            </TextBody>
                            <TextBody>
                                Creating components using atomic design methodology starts by defining the basic
                                building blocks that make up the UI we use to design Houzz. These basic building blocks
                                are called Atoms (buttons, inputs, pills, etc). Atoms can be used independently
                                throughout Houzz or can be stitched together in elegant ways to create Molecules and
                                more complex components. Atoms are used to create Molecules, Molecules are used to
                                create Organisms, and so on.{' '}
                                <HouzzLink href="https://bradfrost.com/blog/post/atomic-web-design/">
                                    Read more about atomic design
                                </HouzzLink>
                            </TextBody>

                            <SectionHeader>Figma Library</SectionHeader>
                            <TextBody>
                                The Houzz Figma library contains both Foundations and Components. What’s the difference?
                            </TextBody>
                            <Text as="p" size="small">
                                Foundations are the visual standards used to define components, layouts, and
                                experiences. Examples of foundations are color, typography, shadows, and corner radius.
                                Foundations are used most frequently to help define how components look and feel.
                            </Text>
                            <TextBody>
                                Components are the collection of reusable building blocks Houzz uses to design
                                experiences for users. Examples include buttons, inputs, dropdowns, modals, and pills.
                                Basic components can be re-used to create more complex components. For example, text,
                                buttons and inputs can be combined to create a modal dialogue for selecting which
                                ideabook a user wants to save to.
                            </TextBody>

                            <SectionHeader>How to use components</SectionHeader>
                            <TextBody>
                                It’s important to remember that the Houzz component library in Figma is only half our
                                design system. Each component in Figma has a corresponding component in Houzz UI (the
                                second half of the design system) that’s used to implement designs in code. The
                                relationship between components in Figma and components in Houzz UI should always be
                                maintained to make sure that implementation matches design. Maintaining this 1:1
                                relationship between design and code ensures clarity in communication between design and
                                development.
                            </TextBody>
                            <TextBody>
                                Each component available in Figma is linked directly to a corresponding component in
                                Houzz UI. When designers use a Figma component in their designs, our engineering team
                                recognizes it by name as a component in Houzz UI. Components are packaged with
                                instructions for behavior (hover state, focused state, accessibility etc.), and have
                                been tested and validated by the team. This workflow makes design handoff and
                                collaboration simple and should reduce the time necessary to build new experiences.
                            </TextBody>

                            <SectionHeader>Contribution</SectionHeader>
                            <TextBody>
                                Designers are encouraged to contribute to Houzz UI. In order to maintain efficiency in
                                our workflow we’ve set guidelines for how and when to contribute to the design system.
                            </TextBody>
                            <TextBody>
                                If there’s a new component or use case, we should review it to see if it should be part
                                of the core system, or see how to extend an existing module to support it.
                            </TextBody>
                        </Box>
                    </TabPanel>
                    <TabPanel>
                        <Box as="section" py="L">
                            <SectionHeader>houzz-ui</SectionHeader>

                            <TextBody>
                                The houzz-ui engineering team’s goal is to help Houzz engineers build applications and
                                web pages faster. To accomplish this, we are introducing some different concepts than
                                you may be familiar with. We also focus on semantics and accessibility (a11y). We do not
                                solve everything for you, but we would like everyone to be aware of these
                                priorities.&nbsp;
                                <code>houzz-ui</code> introduces some different techniques for building applications &
                                web pages at Houzz. Below is a brief review of these technologies.
                            </TextBody>

                            <TextBody>
                                Under the hood, all web and native apps have similar functionality. However, bare
                                bones/3rd party component libraries require lots of work to style properly and
                                consistently, and we are at the mercy of the maintainers to not break our site/app. That
                                is too risky for us! Furthermore, Consumer and Houzz Pro should work the same, but allow
                                flexibility when it comes to the look and feel of components.
                            </TextBody>

                            <SectionHeader>Styled Components (css-in-js) + Theming</SectionHeader>
                            <TextBody>
                                For browser based pages, we will adapt a CSS-in-JS approach leveraging styled-components
                                (possibly @emotion in a future iteration) and styled-system. With styled-components, we
                                will maintain a set of themes that will allow us to tweak how our apps and pages look
                                while using the same set of UI components and design language across teams. More details
                                here. styled-system will be new for most people, but we hope once engineers become
                                familiar with how it works, you will be able to iterate much faster with less code. More
                                information here
                            </TextBody>

                            <SectionHeader>System Props</SectionHeader>
                            <TextBody>
                                A set of consistent props to help developers quickly build out common layouts and UI.
                                For more information, please visit our{' '}
                                <HouzzLink href={withPrefix('/components/system-props')}>System Props</HouzzLink> page.
                            </TextBody>

                            <SectionHeader>Accessibility (a11y)</SectionHeader>
                            <TextBody>
                                Our design system and <code>houzz-ui</code> treat a11y as a first class citizen. Most of
                                our components adhere to strict guidelines to support assistive devices and persons with
                                disabilities. With that said, we can not catch all violations with UI alone. We depend
                                on everyone to ensure your components are usable by everyone. The team will help build
                                utilities if needed outside of the base component library.
                            </TextBody>
                        </Box>
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </Box>
    </Layout>
);

export default Introduction;
